import { useTranslation } from 'react-i18next';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import React, { useState, ChangeEvent, useEffect } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { useDebounce } from 'use-debounce';
import BasicModal from '../common/BasicModal.tsx';
import { Button, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { IManagementStudio } from '../../interfaces/IManagementStudio.ts';
import EditIcon from '@mui/icons-material/Edit';
import ManagementModal from './ManagementModal.tsx';
import IosSwitch from '../common/IosSwitch.tsx';

export default function QualityList() {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setFtchedData] = useState<IManagementStudio[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [deleteQuality, setDeleteQuality] = useState<IManagementStudio>();
	const [addOpen, setAddOpen] = useState<IManagementStudio>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const [qualityLoading, setQualityLoading] = useState(false);
	const [refresh, setRefresh] = useState(1);
	const [isSwitchEnabled, setIsSwitchEnabled] = useState<boolean>(false);
	const handleSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
		setIsSwitchEnabled(event.target.checked);
	};
	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};
	const handleChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
		setAddOpen(prevState => {
			return {
				...prevState,
				title: e.target.value,
			} as IManagementStudio;
		});
	};

	const createNewManagement = () => {
		setAddOpen({} as IManagementStudio);
	};
	const handleCloseDelete = (cat: IManagementStudio) => () => {
		setDeleteQuality(cat);
	};
	const handleClick = (cat: IManagementStudio) => (_: React.MouseEvent<HTMLButtonElement>) => {
		setAddOpen(cat);
	};
	const handleCloseDeleteDialog = () => {
		setDeleteQuality(undefined);
	};
	const handleCloseAddOpen = () => {
		setAddOpen(undefined);
	};

	useEffect(() => {
		setLoading(true);
		axios<IResponse<IManagementStudio[]>>({
			url: '/admin/options/studio_quality',
			params: {
				page,
				limit: pageSize,
				search: JSON.stringify([{ field: 'title', value: search }]),
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFtchedData(res.data.data);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	}, [page, pageSize, search, refresh]);

	const handleClickRegester = () => {
		setQualityLoading(true);
		axios({
			url: addOpen?.id ? `/admin/options/${addOpen?.id}` : '/admin/options',
			method: addOpen?.id ? 'PUT' : 'POST',
			data: {
				title: addOpen?.title,
				system_name: 'studio_quality',
				is_active: isSwitchEnabled,
			},
		}).then(res => {
			if (res.data.success) {
				setRefresh(prev => prev + 1);
				setAddOpen(undefined);
				setQualityLoading(false);
			}
		});
	};
	const handleDeleteCategory = () => {
		setQualityLoading(true);
		axios<IResponse<any>>({
			url: `/admin/options/${deleteQuality?.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteQuality(undefined);
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setQualityLoading(false);
			});
	};

	const properties: (keyof IManagementStudio)[] = ['title'];
	const columns: GridColDef<IManagementStudio>[] = [
		{
			field: 'title',
			headerName: t('title'),
			width: 200,
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('create_date'),
			width: 150,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'is_active',
			headerName: t('status'),
			width: 160,
			headerAlign: 'left',
			headerClassName: `${headerClassNames} ps-12`,
			align: 'left',
			renderCell: params => {
				const statusText = params.value ? t('active') : t('inactive');
				const bgColorClass = params.value ? 'bg-success-selected text-success-main' : 'bg-error-selected text-error-main';
				return <div className={`ms-10 mt-4 flex w-[max-content] items-center rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{statusText}</div>;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'center' as GridAlignment,
			align: 'center' as GridAlignment,
			headerClassName: `${headerClassNames}  pe-10`,
			renderCell: params => {
				return (
					<>
						<IconButton onClick={handleClick(params.row)}>
							<EditIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleCloseDelete(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	return (
		<div className={'w-full'}>
			<BaranDataGrid<IManagementStudio> loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={createNewManagement} actionButtonLable={t('create_new_quality')} searchableFields={properties} />;
			<BasicModal
				open={!!deleteQuality}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={qualityLoading} onClick={handleDeleteCategory} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_quality_description')}</Typography>
			</BasicModal>
			<ManagementModal
				open={!!addOpen}
				onClose={handleCloseAddOpen}
				title={t('create_new_quality')}
				titleClassName={'text-center grow '}
				subClassName={' flex flex-row-reverse'}
				className={'w-full rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseAddOpen} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={qualityLoading} onClick={handleClickRegester} variant="contained">
							{t('register')}
						</LoadingButton>
					</>
				}
			>
				<TextField className={'mb-6 mt-6'} variant={'outlined'} fullWidth label={t('title')} value={addOpen?.title} onChange={handleChangeTitle} />
				<IosSwitch label={t('active')} checked={isSwitchEnabled} onChange={handleSwitchChange} setValue={setIsSwitchEnabled} color="success" />
			</ManagementModal>
		</div>
	);
}
